import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faCheck, faInfoCircle, faUsers } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { formatEuroAmount } from "../../utils/formatEuroAmount";

import { PaymentInterval, PlanCardData } from "./types";

interface PlanCardProps {
	plan: PlanCardData;
	selected: boolean;
	interval: PaymentInterval;
	onSelect: () => void;
}

/**
 * "billed …" copy for the intervals that charge more than one month at a time —
 * each carries the amount actually taken per cycle, which the per-month headline
 * price never shows.
 */
const BILLED_TOTAL_KEY: Record<Exclude<PaymentInterval, "month">, string> = {
	quarter: "usage.subscription.checkout.billedQuarterlyTotal",
	"semi-annual": "usage.subscription.checkout.billedHalfYearlyTotal",
	year: "usage.subscription.checkout.billedYearlyTotal",
};

export const PlanCard: FunctionComponent<PlanCardProps> = ({
	plan,
	selected,
	interval,
	onSelect,
}) => {
	const { t } = useTranslation();
	const { popular } = plan;
	// Only strike through the standard monthly price when it actually beats the
	// selected per-month rate, so non-monthly plans visibly show the discount.
	const showStrikethrough = interval !== "month" && plan.baseMonthlyPrice > plan.monthlyPrice;
	const title = plan.tierLabelKey ? t(plan.tierLabelKey) : plan.name;
	const billedAs =
		interval === "month"
			? t("usage.subscription.checkout.billedMonthly")
			: t(BILLED_TOTAL_KEY[interval], { amount: formatEuroAmount(plan.cycleTotal) });

	const mutedText = popular ? "text-white/80" : "text-text-meta";
	const metaIcon = popular ? "text-white" : "text-blue-action";

	const buttonClass = popular
		? selected
			? "bg-white text-blue-action"
			: "bg-white/20 text-white hover:bg-white/30"
		: selected
			? "bg-blue-action text-white"
			: "bg-background-light text-text hover:bg-border-light";

	return (
		<div
			className={classNames(
				"relative flex flex-col text-left rounded-16 p-5 cursor-pointer transition-all",
				popular
					? "bg-gradient-to-b from-subscription-popular-from to-blue-action text-white border border-transparent"
					: "bg-white text-text border border-border-light",
				selected && !popular ? "border-blue-action ring-4 ring-action-200" : "",
				selected && popular ? "ring-4 ring-action-400" : ""
			)}
			onClick={onSelect}
		>
			{/* Reserved badge row — kept in flow (and in every card) so no plan title
			    can run underneath the badge and all cards stay row-aligned. */}
			<div className="flex justify-end items-start min-h-[26px] mb-1">
				{popular && (
					<span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
						{t("usage.subscription.checkout.mostPopular")}
					</span>
				)}
			</div>

			<div className="text-ssmd font-bold">{title}</div>

			<div className="mt-3">
				<div className="flex items-baseline gap-1">
					<span className="text-md font-bold leading-none">
						{formatEuroAmount(plan.monthlyPrice)}
					</span>
					{showStrikethrough && (
						<span className={classNames("text-sm font-medium line-through", mutedText)}>
							{formatEuroAmount(plan.baseMonthlyPrice)}
						</span>
					)}
					<span className={classNames("text-sm font-medium", mutedText)}>
						{t("usage.subscription.select.perMonth")}
					</span>
				</div>
				<div className={classNames("mt-1.5 text-xs font-medium min-h-4", mutedText)}>
					{billedAs}
				</div>
			</div>

			<div className="flex items-center gap-2 mt-3.5 text-sm font-semibold">
				<Icon className={classNames("w-[17px] h-[17px]", metaIcon)} icon={faBolt} />
				<span>{t("usage.subscription.checkout.creditsPerMonth", { credits: plan.credits })}</span>
			</div>

			<div className="flex items-center gap-2 mt-3 text-sm font-semibold">
				<Icon className={classNames("w-[17px] h-[17px]", metaIcon)} icon={faUsers} />
				<span>{t("usage.subscription.checkout.senders", { count: plan.senders })}</span>
				<Tooltip content={t("usage.subscription.checkout.sendersTooltip")}>
					<Icon className={classNames("w-3.5 h-3.5 cursor-help", mutedText)} icon={faInfoCircle} />
				</Tooltip>
			</div>

			<button
				className={classNames(
					"mt-4 w-full rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors",
					buttonClass
				)}
				type="button"
				onClick={onSelect}
			>
				{selected ? (
					<>
						<Icon className="w-4 h-4" icon={faCheck} />
						{t("usage.subscription.checkout.selectedPlan")}
					</>
				) : (
					t("usage.subscription.checkout.selectPlan", { name: title })
				)}
			</button>
		</div>
	);
};
